import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, Check, Star, Sparkles, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { toast } from 'sonner';

const PLAN_RANK = { individual: 0, basic: 1, standard: 2 };

const planDetails = {
  basic: {
    name: 'Basic',
    price: '$4.95',
    features: ['Unlimited Smart Scans', 'AI Spending Insights', 'Tax Mode with ATO categories', 'Distance & mileage tracking', 'Budget tracking & alerts', 'Bill & subscription management'],
  },
  standard: {
    name: 'Standard',
    price: '$14.95',
    features: ['Everything in Basic', 'Up to 10 users included', 'Employee expense submissions & approvals', 'Invoice management', 'Exportable expense reports', 'Priority support'],
  },
};

export default function UpgradeGate({ feature, requiredPlan, userPlan, children }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const rank = PLAN_RANK[userPlan] ?? 0;
  const required = PLAN_RANK[requiredPlan] ?? 0;

  const { data: trialStatus = {} } = useQuery({
    queryKey: ['trial-status-gate'],
    queryFn: async () => { const { data } = await api.get('/api/trial/status'); return data; },
  });

  if (rank >= required) return children;

  const plan = planDetails[requiredPlan] || planDetails.basic;
  const canTrial = !trialStatus.trial_used;

  const startTrial = async () => {
    setLoading(true);
    try {
      await api.post('/api/trial/start', { plan: requiredPlan });
      toast.success(`7-day free trial started for ${plan.name}!`);
      window.location.reload();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to start trial';
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="upgrade-gate">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white text-center">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Lock className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-bold">{feature} requires {plan.name}</h2>
            <p className="text-amber-100 text-sm mt-1">Upgrade your plan to unlock this feature</p>
          </div>

          {/* Plan card */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{plan.name} Plan</h3>
                <p className="text-sm text-slate-500">Everything you need</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-slate-900">{plan.price}</span>
                <span className="text-slate-500 text-sm">/mo</span>
              </div>
            </div>

            <div className="space-y-2.5 mb-6">
              {plan.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600">{f}</span>
                </div>
              ))}
            </div>

            {/* Trial Button */}
            {canTrial && (
              <button
                onClick={startTrial}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 mb-3 disabled:opacity-50"
                data-testid="trial-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {loading ? 'Starting Trial...' : 'Start 7-Day Free Trial'}
              </button>
            )}

            {/* Subscribe Button */}
            <button
              onClick={() => navigate('/app/settings?tab=subscription')}
              className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${canTrial ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}
              data-testid="upgrade-button"
            >
              {canTrial ? 'Subscribe with PayPal' : `Upgrade to ${plan.name}`}
              <ArrowRight className="w-4 h-4" />
            </button>

            <p className="text-center text-xs text-slate-400 mt-3">
              {canTrial ? 'No credit card required for trial' : `You're on the ${userPlan} plan`}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
